// Formulário de emissão de NF-e com abas.
import React, { useEffect, useMemo, useState } from "react";
import { fiscal } from "../api";
import Destinatario from "../components/Destinatario";
import Itens, { valorItem } from "../components/Itens";
import Tributos from "../components/Tributos";
import { Abas, Aviso, Botao, Campo, Cartao, Entrada, Selecao, Texto, Valor } from "../components/ui";
import { brl, DESTINACOES, MOD_FRETE, REGIMES, round2, soDigitos, UFS } from "../utils";

const INICIAL = {
  tipo_operacao: 1,
  finalidade: 1,
  natureza_operacao: "Venda de mercadoria",
  destinatario: { indicador_ie: 9 },
  itens: [],
  pagamentos: [],
  informacoes_fisco: "",
  observacoes: "",
  valor_desconto: 0, valor_despesas: 0, valor_frete: 0, valor_seguro: 0,
  valor_despesa_aduaneira: 0, valor_desconto_financeiro: 0, integracao_financeira: false,
  consumidor_final: false, presenca: 1, intermediador: 0, tipo_emissao: 1, destinacao: "revenda",
  chaves_referenciadas: [],
  transporte: { modalidade_frete: 9, transportador: {}, veiculo_tracao: null, volumes: [{}], redespacho: {} },
  dados_adicionais: { movimenta_estoque: true },
};

export default function NFeForm({ emissor, onEmitida, notaRejeitada }) {
  const [nota, setNota] = useState(notaRejeitada || INICIAL);
  const [aba, setAba] = useState("identificacao");
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);

  const set = (campo, v) => setNota((n) => ({ ...n, [campo]: v }));
  const setTr = (campo, v) => setNota((n) => ({ ...n, transporte: { ...n.transporte, [campo]: v } }));
  const setDa = (campo, v) => setNota((n) => ({ ...n, dados_adicionais: { ...n.dados_adicionais, [campo]: v } }));

  const ufDestino = nota.destinatario?.endereco?.uf || emissor?.endereco?.uf;
  const contextoTributos = {
    modelo: "55",
    uf_destino: ufDestino,
    consumidor_final: nota.consumidor_final || ["uso_consumo", "ativo"].includes(nota.destinacao)
      || Number(nota.destinatario?.indicador_ie ?? 9) === 9, // não contribuinte = consumidor final (rejeição 696)
    contribuinte_icms: Number(nota.destinatario?.indicador_ie) === 1,
    destinacao: nota.destinacao,
    regime: emissor?.regime_tributario,
  };

  const resumo = useMemo(() => {
    const produtos = round2(nota.itens.reduce((a, i) => a + Number(i.quantidade) * Number(i.valor_unitario), 0));
    const descItens = round2(nota.itens.reduce((a, i) => a + Number(i.desconto || 0), 0));
    const desconto = round2(descItens + Number(nota.valor_desconto || 0));
    const valorNota = round2(produtos - desconto + Number(nota.valor_frete || 0) + Number(nota.valor_seguro || 0) +
      Number(nota.valor_despesas || 0));
    return { produtos, desconto, valorNota, financeiro: round2(valorNota - Number(nota.valor_desconto_financeiro || 0)) };
  }, [nota]);

  // Prévia oficial (servidor): ICMS, ST, DIFAL, IPI e IBS/CBS com a mesma regra da emissão
  const [previa, setPrevia] = useState(null);
  useEffect(() => {
    if (!nota.itens.length) return setPrevia(null);
    const h = setTimeout(() => {
      fiscal.calcularNfe({ ...nota, transporte: limparTransporte(nota.transporte),
        destinatario: { ...nota.destinatario, email: nota.destinatario?.email || null } })
        .then(setPrevia).catch(() => setPrevia(null));
    }, 500);
    return () => clearTimeout(h);
  }, [nota]);
  const valorNota = previa ? previa.valor_nota : resumo.valorNota;
  // VC02-50 (NT 2025.002 v1.52): só a SEFAZ sabe se a nota devolvida foi de saída; aqui é só um aviso, não bloqueia
  const chaveDev = soDigitos(nota.chaves_referenciadas?.[0] || "");
  const emitenteChave = chaveDev.length === 44 ? chaveDev.slice(6, 20) : "";
  const docDest = soDigitos(nota.destinatario?.documento || "");
  const avisoDestinoDevolucao = nota.finalidade === 4 && Number(nota.tipo_operacao) === 1 && emitenteChave && docDest &&
    docDest.padStart(14, "0") !== emitenteChave && soDigitos(emissor?.cnpj || "").padStart(14, "0") !== emitenteChave;
  const avisosItens = [...new Set(nota.itens.flatMap((i) => i.tributos?.avisos || []))];

  // Quando muda o destino (UF/contribuinte), CFOP e alíquotas podem mudar: reaplica a sugestão em todos os itens.
  const reaplicarSugestoes = async () => {
    const itens = await Promise.all(
      nota.itens.map(async (i) => ({ ...i, tributos: await fiscal.sugerirTributos({ ncm: i.ncm, ...contextoTributos }) })),
    );
    set("itens", itens);
  };

  const pendencias = [];
  if (!soDigitos(nota.destinatario?.documento)) pendencias.push("Informe o destinatário");
  if (!nota.destinatario?.endereco?.codigo_municipio) pendencias.push("Endereço do destinatário incompleto (código IBGE)");
  if (!nota.destinatario?.email) pendencias.push("E-mail do destinatário (padrão na NF-e)");
  {
    const de = nota.destinatario || {};
    const ind = Number(de.indicador_ie ?? 9);
    if (ind === 1 && !soDigitos(de.inscricao_estadual)) pendencias.push("Inscrição estadual do destinatário");
    if (ind === 9 && soDigitos(de.documento).length === 14 && !["sefaz_sem_ie", "confirmado_usuario"].includes(de.ie_verificacao)) {
      pendencias.push("Confirme se o destinatário tem inscrição estadual");
    }
  }
  if (nota.itens.length === 0) pendencias.push("Adicione ao menos um item");
  if (nota.finalidade === 4) {  // NT 2025.002 v1.52: devolução referenciada por item
    if (chaveDev.length !== 44) pendencias.push("Chave de acesso (44 dígitos) da nota devolvida");
    const refs = nota.itens.map((i) => Number(i.item_referenciado) || 0);
    if (refs.some((r) => r < 1 || r > 990)) pendencias.push("Nº do item na nota original em todos os itens");
    else if (new Set(refs).size !== refs.length) pendencias.push("O mesmo item da nota original aparece em mais de um item");
  }

  const emitir = async () => {
    setEnviando(true);
    setErro(null);
    try {
      const corpo = {
        ...nota,
        pagamentos: nota.pagamentos.length ? nota.pagamentos : [{ forma: "90", valor: 0 }],
        transporte: limparTransporte(nota.transporte),
        destinatario: { ...nota.destinatario, email: nota.destinatario.email || null },
        informacoes_fisco: nota.informacoes_fisco || null,
        observacoes: nota.observacoes || null,
      };
      // Se a nota já foi rejeitada (agora ou antes), reenvia mantendo o mesmo número.
      const idReenvio = notaRejeitada?.id || (["rejeitada", "erro"].includes(resultado?.status) ? resultado.id : null);
      const r = idReenvio ? await fiscal.reenviar(idReenvio, "55", corpo) : await fiscal.emitirNfe(corpo);
      setResultado(r);
      if (r.status === "autorizada") onEmitida?.(r);
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">{notaRejeitada ? `Corrigir NF-e nº ${notaRejeitada.numero}` : "Nova NF-e"}</h1>
        {emissor?.ambiente === "homologacao" && <span className="rounded bg-amber-100 px-2 py-1 text-xs text-amber-800">Homologação — sem valor fiscal</span>}
      </div>

      <Abas
        ativa={aba}
        onChange={setAba}
        abas={[
          ["identificacao", "Identificação", pendencias.some((p) => p.includes("destinatário"))],
          ["itens", `Itens NF-e (${nota.itens.length})`, nota.itens.length === 0],
          ["transporte", "Transporte"],
          ["tributos", "Tributos"],
          ["adicionais", "Dados Adicionais"],
        ]}
      />

      {aba === "identificacao" && (
        <div className="space-y-4">
          <Cartao titulo="1. Cliente (destinatário)" acoes={nota.itens.length > 0 && <Botao variante="fantasma" onClick={reaplicarSugestoes}>Recalcular tributos dos itens</Botao>}>
            <Destinatario valor={nota.destinatario} onChange={(d) => set("destinatario", d)} modelo="55" />
          </Cartao>

          <Cartao titulo="2. Operação">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <Campo rotulo="Tipo NF-e">
                <Selecao value={nota.tipo_operacao} onChange={(e) => set("tipo_operacao", Number(e.target.value))} opcoes={[[1, "1 — Saída"], [0, "0 — Entrada"]]} />
              </Campo>
              <Campo rotulo="Tipo frete">
                <Selecao value={nota.transporte.modalidade_frete} onChange={(e) => setTr("modalidade_frete", Number(e.target.value))} opcoes={MOD_FRETE} />
              </Campo>
              <Campo rotulo="Natureza de operação" className="md:col-span-2">
                <Entrada value={nota.natureza_operacao} maxLength={60} onChange={(e) => set("natureza_operacao", e.target.value)} />
              </Campo>
              <Campo rotulo="Destinação da mercadoria" dica="Uso e consumo / ativo: consumidor final e DIFAL">
                <Selecao value={nota.destinacao} onChange={(e) => set("destinacao", e.target.value)} opcoes={DESTINACOES} />
              </Campo>
            </div>
          </Cartao>

          <Cartao titulo="3. Valores">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Campo rotulo="Valor desconto" dica="Rateado entre os itens"><Valor value={nota.valor_desconto} onChange={(v) => set("valor_desconto", v)} /></Campo>
              <Campo rotulo="Valor despesa"><Valor value={nota.valor_despesas} onChange={(v) => set("valor_despesas", v)} /></Campo>
              <Campo rotulo="Valor frete"><Valor value={nota.valor_frete} onChange={(v) => set("valor_frete", v)} /></Campo>
              <Campo rotulo="Valor seguro"><Valor value={nota.valor_seguro} onChange={(v) => set("valor_seguro", v)} /></Campo>
              <Campo rotulo="Valor despesa aduaneira"><Valor value={nota.valor_despesa_aduaneira} onChange={(v) => set("valor_despesa_aduaneira", v)} /></Campo>
              <Campo rotulo="Valor desconto financeiro" dica="Não sai na nota; abate do financeiro"><Valor value={nota.valor_desconto_financeiro} onChange={(v) => set("valor_desconto_financeiro", v)} /></Campo>
              <Campo rotulo="Integração financeira">
                <Selecao value={nota.integracao_financeira ? "1" : "0"} onChange={(e) => set("integracao_financeira", e.target.value === "1")} opcoes={[["0", "Não gerar"], ["1", "Gerar contas a receber"]]} />
              </Campo>
            </div>
          </Cartao>

          <Cartao titulo="4. Pagamento">
            <Pagamentos lista={nota.pagamentos} onChange={(p) => set("pagamentos", p)} total={valorNota} />
          </Cartao>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Campo rotulo="Informações adicionais ao fisco"><Texto value={nota.informacoes_fisco || ""} onChange={(e) => set("informacoes_fisco", e.target.value)} /></Campo>
            <Campo rotulo="Observações (informações complementares)"><Texto value={nota.observacoes || ""} onChange={(e) => set("observacoes", e.target.value)} /></Campo>
          </div>
        </div>
      )}

      {aba === "itens" && <Itens itens={nota.itens} onChange={(i) => set("itens", i)} contexto={contextoTributos} devolucao={nota.finalidade === 4} />}

      {aba === "transporte" && <AbaTransporte tr={nota.transporte} setTr={setTr} />}

      {aba === "tributos" && (
        <div className="space-y-4">
          <Aviso>
            Tributos sugeridos pelo NCM, regime ({(REGIMES.find(([k]) => k === String(emissor?.regime_tributario)) || [])[1] || emissor?.regime_tributario}) e destino ({ufDestino}). Ajuste item a item se preciso.
          </Aviso>
          <div className="flex justify-end">
            <Botao variante="secundario" onClick={reaplicarSugestoes} disabled={!nota.itens.length}>Reaplicar sugestão em todos os itens</Botao>
          </div>
          {nota.itens.map((i, idx) => (
            <Cartao key={idx} titulo={`${idx + 1}. ${i.descricao} — NCM ${i.ncm} — ${brl(valorItem(i))}`}>
              <Tributos
                valor={i.tributos}
                regime={emissor?.regime_tributario}
                onChange={(t) => set("itens", nota.itens.map((x, k) => (k === idx ? { ...x, tributos: t } : x)))}
              />
            </Cartao>
          ))}
        </div>
      )}

      {aba === "adicionais" && (
        <Cartao titulo="Dados adicionais">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <Campo rotulo="Departamento"><Entrada value={nota.dados_adicionais.departamento || ""} onChange={(e) => setDa("departamento", e.target.value)} /></Campo>
            <Campo rotulo="Centro de custo"><Entrada value={nota.dados_adicionais.centro_custo || ""} onChange={(e) => setDa("centro_custo", e.target.value)} /></Campo>
            <Campo rotulo="Local de estoque"><Entrada value={nota.dados_adicionais.local_estoque || ""} onChange={(e) => setDa("local_estoque", e.target.value)} /></Campo>
            <Campo rotulo="Local de estoque destino"><Entrada value={nota.dados_adicionais.local_estoque_destino || ""} onChange={(e) => setDa("local_estoque_destino", e.target.value)} /></Campo>
            <Campo rotulo="Movimenta estoque">
              <Selecao value={nota.dados_adicionais.movimenta_estoque ? "1" : "0"} onChange={(e) => setDa("movimenta_estoque", e.target.value === "1")} opcoes={[["1", "Sim"], ["0", "Não"]]} />
            </Campo>
            <Campo rotulo="Tabela de preços"><Entrada value={nota.dados_adicionais.tabela_precos || ""} onChange={(e) => setDa("tabela_precos", e.target.value)} /></Campo>
            <Campo rotulo="Data prevista de entrega"><Entrada type="date" value={nota.dados_adicionais.data_prevista_entrega || ""} onChange={(e) => setDa("data_prevista_entrega", e.target.value || null)} /></Campo>
            <Campo rotulo="Município IBS/CBS (IBGE)"><Entrada value={nota.dados_adicionais.municipio_ibs_cbs || ""} maxLength={7} onChange={(e) => setDa("municipio_ibs_cbs", soDigitos(e.target.value))} /></Campo>
            <Campo rotulo="Tipo nota débito"><Entrada value={nota.dados_adicionais.tipo_nota_debito || ""} onChange={(e) => setDa("tipo_nota_debito", e.target.value)} /></Campo>
            <Campo rotulo="Tipo nota crédito"><Entrada value={nota.dados_adicionais.tipo_nota_credito || ""} onChange={(e) => setDa("tipo_nota_credito", e.target.value)} /></Campo>
            <Campo rotulo="Local de operação"><Entrada value={nota.dados_adicionais.local_operacao || ""} onChange={(e) => setDa("local_operacao", e.target.value)} /></Campo>
            <Campo rotulo="Consumidor final" dica={Number(nota.destinatario?.indicador_ie ?? 9) === 9 ? "Destinatário não contribuinte: sempre consumidor final" : undefined}>
              <Selecao value={nota.consumidor_final || Number(nota.destinatario?.indicador_ie ?? 9) === 9 ? "1" : "0"} onChange={(e) => set("consumidor_final", e.target.value === "1")} opcoes={[["0", "0 — Normal"], ["1", "1 — Consumidor final"]]} />
            </Campo>
            <Campo rotulo="Presença do comprador">
              <Selecao value={nota.presenca} onChange={(e) => set("presenca", Number(e.target.value))}
                opcoes={[[0, "0 — Não se aplica"], [1, "1 — Presencial"], [2, "2 — Internet"], [3, "3 — Teleatendimento"], [4, "4 — Entrega a domicílio"], [9, "9 — Outros"]]} />
            </Campo>
            <Campo rotulo="Indicador de intermediador">
              <Selecao value={nota.intermediador} onChange={(e) => set("intermediador", Number(e.target.value))}
                opcoes={[[0, "0 — Sem intermediador (site próprio)"], [1, "1 — Com intermediador (marketplace)"]]} />
            </Campo>
            {nota.intermediador === 1 && (
              <>
                <Campo rotulo="CNPJ do intermediador"><Entrada value={nota.intermediador_cnpj || ""} onChange={(e) => set("intermediador_cnpj", soDigitos(e.target.value))} /></Campo>
                <Campo rotulo="Identificador no intermediador"><Entrada value={nota.intermediador_identificador || ""} onChange={(e) => set("intermediador_identificador", e.target.value)} /></Campo>
              </>
            )}
            <Campo rotulo="Tipo ambiente" dica="Definido em Configurações fiscais">
              <Entrada value={emissor?.ambiente === "producao" ? "1 — Produção" : "2 — Homologação"} disabled />
            </Campo>
            <Campo rotulo="Finalidade NF-e">
              <Selecao value={nota.finalidade} onChange={(e) => set("finalidade", Number(e.target.value))}
                opcoes={[[1, "1 — Normal"], [2, "2 — Complementar"], [3, "3 — Ajuste"], [4, "4 — Devolução"]]} />
            </Campo>
            <Campo rotulo="Tipo de emissão">
              <Selecao value={nota.tipo_emissao} onChange={(e) => set("tipo_emissao", Number(e.target.value))}
                opcoes={[[1, "1 — Normal"], [6, "6 — Contingência SVC-AN"], [7, "7 — Contingência SVC-RS"]]} />
            </Campo>
            {nota.finalidade !== 1 && (
              <Campo rotulo="Chave da nota referenciada" className="md:col-span-2"
                dica={nota.finalidade === 4 ? "Chave da nota que está sendo devolvida. Em cada item, informe o nº dele nessa nota." : undefined}>
                <Entrada value={nota.chaves_referenciadas[0] || ""} maxLength={44}
                  onChange={(e) => set("chaves_referenciadas", [soDigitos(e.target.value)].filter(Boolean))} />
              </Campo>
            )}
            {avisoDestinoDevolucao && (
              <div className="md:col-span-2" data-testid="aviso-vc0250">
                <Aviso>Atenção: o destinatário não é o emitente da nota devolvida. Se essa nota foi de saída, a SEFAZ rejeita
                  (rejeição 1194): na devolução, o destinatário deve ser quem emitiu a nota original.</Aviso>
              </div>
            )}
          </div>
        </Cartao>
      )}

      {/* Resumo fixo + emissão */}
      <div className="sticky bottom-0 z-10 rounded-lg border bg-white p-4 shadow-lg border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <dl className="flex flex-wrap gap-6 text-sm">
            <div><dt className="text-gray-500">Valor produtos</dt><dd className="font-semibold tabular-nums">{brl(resumo.produtos)}</dd></div>
            <div><dt className="text-gray-500">Descontos</dt><dd className="tabular-nums">{brl(resumo.desconto)}</dd></div>
            {previa && <div><dt className="text-gray-500">ICMS</dt><dd className="tabular-nums">{brl(previa.vICMS)}</dd></div>}
            {previa?.vST > 0 && <div><dt className="text-gray-500">ICMS ST</dt><dd className="tabular-nums">{brl(previa.vST + previa.vFCPST)}</dd></div>}
            {previa?.vICMSUFDest > 0 && <div><dt className="text-gray-500">DIFAL</dt><dd className="tabular-nums">{brl(previa.vICMSUFDest + previa.vFCPUFDest)}</dd></div>}
            {previa?.vIPI > 0 && <div><dt className="text-gray-500">IPI</dt><dd className="tabular-nums">{brl(previa.vIPI)}</dd></div>}
            {previa?.ibs_cbs_incluido && <div><dt className="text-gray-500">IBS + CBS</dt><dd className="tabular-nums">{brl((previa.vIBS || 0) + (previa.vCBS || 0))}</dd></div>}
            <div><dt className="text-gray-500">Valor NF-e</dt><dd className="text-lg font-bold tabular-nums">{brl(valorNota)}</dd></div>
            <div><dt className="text-gray-500">Valor financeiro</dt><dd className="tabular-nums">{brl(previa ? previa.valor_financeiro : resumo.financeiro)}</dd></div>
          </dl>
          <Botao carregando={enviando} disabled={pendencias.length > 0} onClick={emitir} className="px-6">
            {notaRejeitada ? "Reenviar à SEFAZ" : "Emitir NF-e"}
          </Botao>
        </div>
        {pendencias.length > 0 && <p className="mt-2 text-xs text-gray-500">Pendências: {pendencias.join(" · ")}</p>}
        {avisosItens.length > 0 && <p className="mt-2 text-xs text-amber-800">Atenção: {avisosItens.join(" · ")}</p>}
        {previa?.difal_destinatario > 0 && (
          <p className="mt-2 text-xs text-gray-600">DIFAL de responsabilidade do destinatário (uso e consumo): {brl(previa.difal_destinatario)} — sai nas informações complementares.</p>
        )}
        {erro && <div className="mt-3"><Aviso tipo="erro">{erro}</Aviso></div>}
        {resultado && <div className="mt-3"><ResultadoEmissao nota={resultado} /></div>}
      </div>
    </div>
  );
}

function limparTransporte(tr) {
  const vazio = (o) => !o || Object.values(o).every((v) => v === "" || v === null || v === undefined);
  return {
    ...tr,
    transportador: vazio(tr.transportador) ? null : tr.transportador,
    veiculo_tracao: tr.veiculo_tracao?.placa ? tr.veiculo_tracao : null,
    redespacho: vazio(tr.redespacho) ? null : tr.redespacho,
    volumes: (tr.volumes || []).filter((v) => !vazio(v)),
  };
}

function AbaTransporte({ tr, setTr }) {
  const t = tr.transportador || {};
  const v = tr.veiculo_tracao || {};
  const vol = (tr.volumes || [{}])[0] || {};
  const r = tr.redespacho || {};
  const setT = (c, x) => setTr("transportador", { ...t, [c]: x });
  const setV = (c, x) => setTr("veiculo_tracao", { ...v, [c]: x });
  const setVol = (c, x) => setTr("volumes", [{ ...vol, [c]: x }]);
  const setR = (c, x) => setTr("redespacho", { ...r, [c]: x });
  const numero = (setter, c) => (e) => setter(c, e.target.value === "" ? null : Number(e.target.value));

  return (
    <div className="space-y-4">
      <Cartao titulo="Transportador">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          <Campo rotulo="CPF/CNPJ" className="md:col-span-2"><Entrada value={t.documento || ""} onChange={(e) => setT("documento", soDigitos(e.target.value))} /></Campo>
          <Campo rotulo="Nome / Razão social" className="md:col-span-4"><Entrada value={t.nome || ""} onChange={(e) => setT("nome", e.target.value)} /></Campo>
          <Campo rotulo="Inscrição estadual" className="md:col-span-2"><Entrada value={t.inscricao_estadual || ""} onChange={(e) => setT("inscricao_estadual", e.target.value)} /></Campo>
          <Campo rotulo="Endereço" className="md:col-span-2"><Entrada value={t.endereco || ""} onChange={(e) => setT("endereco", e.target.value)} /></Campo>
          <Campo rotulo="Município"><Entrada value={t.municipio || ""} onChange={(e) => setT("municipio", e.target.value)} /></Campo>
          <Campo rotulo="UF"><Selecao value={t.uf || ""} vazio="--" onChange={(e) => setT("uf", e.target.value)} opcoes={UFS.map((u) => [u, u])} /></Campo>
        </div>
      </Cartao>
      <Cartao titulo="Veículo tração">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Campo rotulo="Placa"><Entrada value={v.placa || ""} maxLength={8} onChange={(e) => setV("placa", e.target.value.toUpperCase())} /></Campo>
          <Campo rotulo="UF"><Selecao value={v.uf || ""} vazio="--" onChange={(e) => setV("uf", e.target.value)} opcoes={UFS.map((u) => [u, u])} /></Campo>
          <Campo rotulo="RNTC (ANTT)"><Entrada value={v.rntc || ""} onChange={(e) => setV("rntc", e.target.value)} /></Campo>
        </div>
      </Cartao>
      <Cartao titulo="Volumes">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
          <Campo rotulo="Espécie"><Entrada value={vol.especie || ""} onChange={(e) => setVol("especie", e.target.value)} /></Campo>
          <Campo rotulo="Marca"><Entrada value={vol.marca || ""} onChange={(e) => setVol("marca", e.target.value)} /></Campo>
          <Campo rotulo="Numeração"><Entrada value={vol.numeracao || ""} onChange={(e) => setVol("numeracao", e.target.value)} /></Campo>
          <Campo rotulo="Qtd. volumes"><Entrada type="number" min="0" value={vol.quantidade ?? ""} onChange={numero(setVol, "quantidade")} /></Campo>
          <Campo rotulo="Peso bruto (kg)"><Entrada type="number" step="0.001" min="0" value={vol.peso_bruto ?? ""} onChange={numero(setVol, "peso_bruto")} /></Campo>
          <Campo rotulo="Peso líquido (kg)"><Entrada type="number" step="0.001" min="0" value={vol.peso_liquido ?? ""} onChange={numero(setVol, "peso_liquido")} /></Campo>
        </div>
      </Cartao>
      <Cartao titulo="Transporte redespacho">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Campo rotulo="CPF/CNPJ"><Entrada value={r.documento || ""} onChange={(e) => setR("documento", soDigitos(e.target.value))} /></Campo>
          <Campo rotulo="Nome / Razão social" className="md:col-span-2" dica="Sai nas informações complementares da nota">
            <Entrada value={r.nome || ""} onChange={(e) => setR("nome", e.target.value)} />
          </Campo>
        </div>
      </Cartao>
    </div>
  );
}

export function Pagamentos({ lista, onChange, total }) {
  const formas = [["01", "Dinheiro"], ["03", "Cartão de crédito"], ["04", "Cartão de débito"], ["17", "PIX"],
    ["15", "Boleto"], ["05", "Crédito loja"], ["16", "Depósito"], ["18", "Transferência"], ["90", "Sem pagamento"], ["99", "Outros"]];
  const soma = round2(lista.reduce((a, p) => a + Number(p.valor || 0), 0));
  return (
    <div className="space-y-2">
      {lista.map((p, i) => (
        <div key={i} className="flex flex-wrap items-end gap-2">
          <Campo rotulo="Forma" className="w-48">
            <Selecao value={p.forma} onChange={(e) => onChange(lista.map((x, k) => (k === i ? { ...x, forma: e.target.value } : x)))} opcoes={formas} />
          </Campo>
          <Campo rotulo="Valor" className="w-36">
            <Valor value={p.valor} onChange={(v) => onChange(lista.map((x, k) => (k === i ? { ...x, valor: v } : x)))} />
          </Campo>
          <Botao variante="fantasma" className="text-red-600" onClick={() => onChange(lista.filter((_, k) => k !== i))}>Remover</Botao>
        </div>
      ))}
      <div className="flex items-center gap-4">
        <Botao variante="secundario" onClick={() => onChange([...lista, { forma: "17", valor: round2(Math.max(total - soma, 0)) }])}>+ Forma de pagamento</Botao>
        {lista.length > 0 && Math.abs(soma - total) > 0.009 && (
          <span className="text-xs text-amber-700">Pagamentos somam {brl(soma)}; nota {brl(total)}</span>
        )}
      </div>
    </div>
  );
}

export function ResultadoEmissao({ nota }) {
  if (nota.status === "autorizada") {
    return (
      <Aviso tipo="sucesso">
        {nota.modelo === "65" ? "NFC-e" : "NF-e"} nº {nota.numero} autorizada. Protocolo {nota.protocolo}.{"\n"}Chave: {nota.chave}
      </Aviso>
    );
  }
  if (nota.status === "processando") {
    return <Aviso tipo="atencao">Nota enviada e ainda em processamento na SEFAZ. Acompanhe na lista de notas (Buscar protocolo).</Aviso>;
  }
  return (
    <Aviso tipo="erro">
      Nota {nota.status}: {nota.codigo_status ? `[${nota.codigo_status}] ` : ""}{nota.motivo_status}
      {"\n"}Corrija os dados e reenvie — o número {nota.numero} será mantido.
    </Aviso>
  );
}
